// "Connect repository" (existing-project onboarding, spec §3.2) and the legacy "New project from GitHub" dialog: pick an
// active GitHub connection, then a repository it reaches (GithubRepositoryPicker). Create posts /v1/projects/from-github
// with one Idempotency-Key per opened dialog and opens the new project's settings, where the provisioning progress
// lives; connect posts the project's provision and closes (the caller shows the progress). The connect dialog is
// prefilled with the project's GitHub repository. Organization admins only (the API enforces it). Since D36 the New
// project dialog offers the GitHub import as its first choice (CreateProjectDialog).
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import type { ProjectSummary } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { createProjectFromGithub, provisionRepository } from "../../api/endpoints-onboarding.ts";
import { newId } from "../../lib/storage.ts";
import { Dialog } from "../../components/Dialog.tsx";
import { Button } from "../../components/Button.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { useToast } from "../../components/Toast.tsx";
import { GithubRepositoryPicker, useGithubPicker } from "./GithubRepositoryPicker.tsx";

/** `owner/repo` of a GitHub HTTPS URL (with or without `.git`), or null for anything else. */
export function githubFullName(url: string | null | undefined): string | null {
  return /^https:\/\/github\.com\/([^/\s]+\/[^/\s]+?)(?:\.git)?\/?$/i.exec(url ?? "")?.[1] ?? null;
}

export function GithubProjectDialog({ open, onClose, mode, project }: { open: boolean; onClose: () => void; mode: "create" | "connect"; project?: ProjectSummary }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [key, setKey] = useState(newId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const picker = useGithubPicker(open, githubFullName(project?.repository_url) ?? "");
  const { connection, repository } = picker;

  useEffect(() => {
    if (!open) return;
    setKey(newId());
    setError(null);
  }, [open]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!connection || !repository) return;
    setBusy(true);
    setError(null);
    const body = { integration_id: connection.id, repository: repository.fullName };
    try {
      if (mode === "create") {
        const created = await createProjectFromGithub(body, key);
        toast(t("githubOnboarding.created", { name: created.project.name }), "success");
        onClose();
        navigate(`/projects/${created.project.id}/settings`);
      } else if (project) {
        await provisionRepository(project.id, body);
        onClose();
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const formId = "github-project-form";
  const create = mode === "create";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      busy={busy}
      testId="github-project-dialog"
      title={create ? t("githubOnboarding.newFromGithub") : t("githubOnboarding.connectTitle")}
      description={create ? t("githubOnboarding.createDescription") : t("githubOnboarding.connectDescription")}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={busy} disabled={!repository}>
            {create ? t("githubOnboarding.create") : t("githubOnboarding.connect")}
          </Button>
        </>
      }
    >
      <form id={formId} className="stack" onSubmit={onSubmit} noValidate>
        {error ? <ActionError error={error} /> : null}
        <GithubRepositoryPicker picker={picker} onLeave={onClose} />
      </form>
    </Dialog>
  );
}
